import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ClipboardList, Search } from "lucide-react";

import { useAuth } from "../../app/auth.js";
import { api } from "../../lib/api.js";
import {
  formatAuditDate,
  formatAuditTime,
  getPayrollAuditPresentation,
  payrollAuditFilterSummary,
  payrollAuditFiltersFromSearch,
  type PayrollAuditEventLike,
} from "../../lib/payrollAuditDisplay.js";
import type { PayrollAuditKind } from "./PayrollAuditLinks.js";

type AuditEvent = PayrollAuditEventLike & {
  id: number;
  actorDisplayName: string;
  action: string;
  targetType?: string;
  targetIdentifier?: string;
  beforePayload?: Record<string, unknown>;
  afterPayload?: Record<string, unknown>;
  reason: string;
  context?: Record<string, unknown>;
  createdAt: string;
};

export const payrollAuditLevelConfig: Record<
  PayrollAuditKind,
  {
    label: string;
    title: string;
    description: string;
    countLabel: string;
    emptyLabel: string;
    searchPlaceholder: string;
  }
> = {
  business: {
    label: "All activity",
    title: "All activity",
    description: "",
    countLabel: "activities",
    emptyLabel: "No payroll activity matches this context.",
    searchPlaceholder: "Search activity, person or reason",
  },
  changes: {
    label: "Manual changes",
    title: "Manual changes",
    description: "",
    countLabel: "manual changes",
    emptyLabel: "No manual changes match this context.",
    searchPlaceholder: "Search change, person or reason",
  },
  technical: {
    label: "System records",
    title: "System records",
    description: "",
    countLabel: "system records",
    emptyLabel: "No system records match this context.",
    searchPlaceholder: "Search event code, record or payload",
  },
};

const technicalPayload = (event: AuditEvent) =>
  JSON.stringify(
    {
      eventId: event.id,
      action: event.action,
      actor: event.actorDisplayName,
      targetType: event.targetType,
      targetIdentifier: event.targetIdentifier,
      reason: event.reason || undefined,
      before: event.beforePayload,
      after: event.afterPayload,
      context: event.context,
      createdAt: event.createdAt,
    },
    null,
    2,
  );

const eventSearchText = (event: AuditEvent) => {
  const presentation = getPayrollAuditPresentation(event);
  return JSON.stringify({
    label: presentation.label,
    description: presentation.description,
    explanation: presentation.explanation,
    action: event.action,
    actor: presentation.actorName,
    scope: presentation.scopeLabel,
    month: presentation.month,
    monthLabel: presentation.monthLabel,
    target: presentation.targetLabel,
    reason: event.reason,
    before: event.beforePayload,
    after: event.afterPayload,
    context: event.context,
  }).toLowerCase();
};

function PayrollAuditEvent({
  event,
  level,
}: {
  event: AuditEvent;
  level: PayrollAuditKind;
}) {
  const presentation = getPayrollAuditPresentation(event);
  const actorUsername = String(event.context?.["actorUsername"] ?? "").trim();
  const actorEmail = String(event.context?.["actorEmail"] ?? "").trim();
  const actorAccount = actorUsername || actorEmail || presentation.actorName;
  return (
    <li className="payroll-audit-event">
      <details className="group">
        <summary className="payroll-audit-event__summary">
          <span className="payroll-audit-event__content">
            <span className="payroll-audit-event__heading">
              <strong>{presentation.label}</strong>
            </span>
            <span className="payroll-audit-event__description">
              {presentation.description}
            </span>
            {level === "technical" && (
              <code className="payroll-audit-event__code">{event.action}</code>
            )}
          </span>

          <span className="payroll-audit-event__fact">
            <span>Changed by</span>
            <strong>{presentation.actorName}</strong>
            <small>{actorAccount}</small>
          </span>

          <span className="payroll-audit-event__fact">
            <span>Affected</span>
            <strong>{presentation.scopeLabel}</strong>
          </span>

          <span className="payroll-audit-event__fact">
            <span>Payroll month</span>
            <strong>{presentation.monthLabel}</strong>
          </span>

          <time
            className="payroll-audit-event__fact payroll-audit-event__time"
            dateTime={event.createdAt}
          >
            <span>Date and time</span>
            <strong>{formatAuditDate(event.createdAt)}</strong>
            <small>{formatAuditTime(event.createdAt)}</small>
          </time>

          <ChevronDown
            size={18}
            className="payroll-audit-event__chevron"
            aria-hidden="true"
          />
        </summary>

        <div className="payroll-audit-event__details">
          <div className="payroll-audit-event__explanation">
            <span>{presentation.explanationLabel}</span>
            <p>{presentation.explanation}</p>
          </div>
          <dl className="payroll-audit-event__facts">
            <div>
              <dt>Affected record</dt>
              <dd>{presentation.targetLabel || presentation.scopeLabel}</dd>
            </div>
            <div>
              <dt>Recorded by</dt>
              <dd>
                {presentation.actorName}
                {actorAccount !== presentation.actorName
                  ? ` (${actorAccount})`
                  : ""}
              </dd>
            </div>
            <div>
              <dt>Payroll month</dt>
              <dd>{presentation.monthLabel}</dd>
            </div>
          </dl>
          {level === "technical" && (
            <div className="payroll-audit-event__payload">
              <div>
                <span>
                  Event <code>#{event.id}</code>
                </span>
                <span>
                  Target{" "}
                  <code>
                    {event.targetType || "record"}:
                    {event.targetIdentifier || "unknown"}
                  </code>
                </span>
              </div>
              <pre aria-label={`Technical evidence for ${event.action}`}>
                {technicalPayload(event)}
              </pre>
            </div>
          )}
        </div>
      </details>
    </li>
  );
}

export function PayrollAuditPage({
  level,
  embedded = false,
}: {
  level: PayrollAuditKind;
  embedded?: boolean;
}) {
  const authQuery = useAuth();
  const filters = payrollAuditFiltersFromSearch(
    typeof window === "undefined" ? "" : window.location.search,
    level,
  );
  const accessQuery = useQuery({
    queryKey: ["openproject", "payroll-access"],
    queryFn: () => api.getOpenProjectPayrollAccess(),
    enabled: Boolean(authQuery.data?.user),
    retry: false,
  });
  const technicalAllowed = Boolean(accessQuery.data?.officeAccess);
  const auditQuery = useQuery({
    queryKey: ["openproject", "payroll-audit", filters],
    queryFn: () => api.getOpenProjectPayrollAudit(filters),
    enabled: Boolean(
      accessQuery.data?.officeAccess &&
      (level !== "technical" || technicalAllowed),
    ),
    retry: false,
  });
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(100);
  const config = payrollAuditLevelConfig[level]!;

  useEffect(() => setVisibleCount(100), [search, level]);

  const events: AuditEvent[] = auditQuery.data?.events ?? [];
  const filteredEvents = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return normalizedSearch
      ? events.filter((event) =>
          eventSearchText(event).includes(normalizedSearch),
        )
      : events;
  }, [events, search]);
  const visibleEvents = filteredEvents.slice(0, visibleCount);
  const filterSummary = payrollAuditFilterSummary(filters);

  const statusContent =
    authQuery.isLoading || accessQuery.isLoading ? (
      <p className="payroll-audit-state">Checking payroll audit access…</p>
    ) : !accessQuery.data?.officeAccess ? (
      <p className="payroll-audit-state payroll-audit-state--error">
        Payroll office access is required to view the audit.
      </p>
    ) : level === "technical" && !technicalAllowed ? (
      <p className="payroll-audit-state payroll-audit-state--error">
        System records are available only to system administrators with payroll
        office access.
      </p>
    ) : null;

  const feed = (
    <section
      className="payroll-audit-workspace"
      aria-label={`${config.label} payroll audit`}
    >
      <div className="payroll-audit-toolbar">
        <p className="payroll-audit-toolbar__count">
          {auditQuery.isLoading
            ? "Loading records..."
            : `${filteredEvents.length} ${config.countLabel}`}
        </p>
        <label className="payroll-audit-search">
          <span className="sr-only">Search {config.label.toLowerCase()}</span>
          <Search size={16} aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={config.searchPlaceholder}
          />
        </label>
      </div>
      {!embedded && (
        <div className="payroll-audit-context">
          <span>
            {filterSummary.length
              ? filterSummary.join(", ")
              : "All payroll activity"}
          </span>
        </div>
      )}
      {statusContent}
      {auditQuery.isError && (
        <p className="payroll-audit-state payroll-audit-state--error">
          {auditQuery.error instanceof Error
            ? auditQuery.error.message
            : "The payroll audit could not be loaded."}
        </p>
      )}
      {!statusContent && (
        <ol className="payroll-audit-feed">
          {visibleEvents.map((event) => (
            <PayrollAuditEvent key={event.id} event={event} level={level} />
          ))}
        </ol>
      )}
      {!statusContent &&
        !auditQuery.isLoading &&
        filteredEvents.length === 0 && (
          <div className="payroll-audit-empty">
            <ClipboardList size={22} aria-hidden="true" />
            <p>
              {events.length === 0
                ? config.emptyLabel
                : "No events match your search."}
            </p>
          </div>
        )}
      {visibleEvents.length < filteredEvents.length && (
        <div className="payroll-audit-load-more">
          <button
            type="button"
            onClick={() => setVisibleCount((count) => count + 100)}
          >
            Load 100 more
          </button>
        </div>
      )}
    </section>
  );

  return feed;
}
